import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { IoSaveOutline, IoSettingsOutline, IoInformationCircleOutline } from "react-icons/io5";
import { toast } from "react-hot-toast";
import { getSystemSettings, updateSystemSettings } from "../api/apiServices";

export default function SystemSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    deliveryFeePerKm: "",
    customerDeliveryFeePerKm: "",
    vendorFee: "",
    platformCommission: "",
    minOrderAmount: "",
    platformDeliverySubsidy: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await getSystemSettings();
      // Ensure we keep them as strings/numbers that can be edited
      if (data) setSettings(data);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // ALLOW empty string so users can delete and type
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Convert strings back to numbers for the API
    const payload = {
      deliveryFeePerKm: Number(settings.deliveryFeePerKm) || 0,
      customerDeliveryFeePerKm: Number(settings.customerDeliveryFeePerKm) || 0,
      vendorFee: Number(settings.vendorFee) || 0,
      platformCommission: Number(settings.platformCommission) || 0,
      minOrderAmount: Number(settings.minOrderAmount) || 0,
      platformDeliverySubsidy: Number(settings.platformDeliverySubsidy) || 0,
    };

    try {
      await updateSystemSettings(payload);
      toast.success("Settings updated successfully!");
      // Refresh state with cleaned numbers
      setSettings(payload);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center">Loading Settings...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-12">
      <div className="max-w-4xl mx-auto">
        <header className="mb-10">
          <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-3">
            <IoSettingsOutline className="text-indigo-600" />
            System Configuration
          </h1>
          <p className="text-gray-500 mt-2">
            Manage global delivery rates, commissions, and platform subsidies.
          </p>
        </header>

        <motion.form 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSave}
          className="bg-white shadow-xl rounded-3xl overflow-hidden border border-gray-100"
        >
          <div className="p-8 space-y-8">
            
            {/* Financial Controls */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Revenue & Commissions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup
                  label="Platform Commission (%)"
                  name="platformCommission"
                  value={settings.platformCommission}
                  onChange={handleChange}
                  suffix="%"
                  tooltip="Percentage taken from the total order subtotal."
                />
                <InputGroup
                  label="Vendor Fixed Fee (₦)"
                  name="vendorFee"
                  value={settings.vendorFee}
                  onChange={handleChange}
                  prefix="₦"
                  tooltip="Fixed service fee charged to vendors per order."
                />
              </div>
            </section>

            {/* Logistics Controls */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Logistics & Delivery</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup
                  label="Delivery Fee Per KM (₦)"
                  name="deliveryFeePerKm"
                  value={settings.deliveryFeePerKm}
                  onChange={handleChange}
                  prefix="₦"
                  tooltip="Cost applied for every kilometer of delivery distance."
                />
                
                <InputGroup
                  label="Delivery Fee Per KM (%) for user"
                  name="customerDeliveryFeePerKm"
                  value={settings.customerDeliveryFeePerKm}
                  onChange={handleChange}
                  suffix="%"
                  tooltip="percentage user pays for every kilometer of delivery distance."
                />
                <InputGroup
                  label="Platform Delivery Subsidy (%) for platform"
                  name="platformDeliverySubsidy"
                  value={settings.platformDeliverySubsidy}
                  onChange={handleChange}
                  prefix="%"
                  tooltip="percentage the platform pays towards delivery to reduce customer cost."
                />
                <InputGroup
                  label="Minimum Order Amount (₦)"
                  name="minOrderAmount"
                  value={settings.minOrderAmount}
                  onChange={handleChange}
                  prefix="₦"
                />
              </div>
            </section>
          </div>

          <div className="bg-gray-50 p-6 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-lg shadow-indigo-200 disabled:bg-gray-400"
            >
              {saving ? "Saving..." : <><IoSaveOutline size={20} /> Save Settings</>}
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}

// Reusable Input Component
function InputGroup({ label, name, value, onChange, prefix, suffix, tooltip }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
        {label}
        {tooltip && (
          <span className="text-gray-400 cursor-help group relative">
            <IoInformationCircleOutline size={16} />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-gray-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              {tooltip}
            </span>
          </span>
        )}
      </label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">{prefix}</span>}
        <input
          type="number"
          name={name}
          value={value}
          onChange={onChange}
          className={`w-full border border-gray-200 rounded-xl py-2.5 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${prefix ? 'pl-8' : 'px-4'} ${suffix ? 'pr-8' : ''}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">{suffix}</span>}
      </div>
    </div>
  );
}